import React, { useState } from "react";
import cx from "classnames";
import dayjs from "dayjs";
import useDeepCompareEffect from "use-deep-compare-effect";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMapMarkerAlt, faTimes } from "@fortawesome/free-solid-svg-icons";
import { debounce } from "throttle-debounce";
import { AppContext } from "../../../context";
import getDestinations from "./utils/getDestinations";
import getInitialTo from "./utils/getInitialTo";
import toFieldAdapter from "./ToField.adapter";
import { InputWrapper } from "../../../components/InputWrapper";
import { Typeahead } from "../../../components/Typeahead";
import { BRANDS, MODES } from "../../../configs";
import {loadSOVGatewayData} from "../../Shared/utils/loadSOVGatewayData";
import {transformSOVGatewayData} from "../../Shared/utils/transformSOVGatewayData";

const ToField = ({
	id,
	mode,
	index,
	selectedType,
	className,
	classnames,

	error,
	isOnlyOneTrue,
	isSetMultipleFrom,

	selectedTo,
	selectedFrom,
	previousSessionValue,
	multipleFlightData,

	onChange,
	resetError,
	handleFocus,
	setMultipleFlightData,
	setNextButtonDisabled,
	setValidateMultipleCities,
	handleSovMobileInputSelectionToView,
}) => {
	const [useAppState] = AppContext.useAppContext();
	const [typeaheadData, setTypeaheadData] = useState([]);
	const [initialGateway, setInitialGateway] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isHasSelectedItem, setIsHasSelectedItem] = useState(!!selectedTo?.label ?? false);

	const handleSOVInput = debounce(200, async searchQuery => {
		if (useAppState.brand === BRANDS.SOV) {
			resetError();

			if (searchQuery) {
				const gateways = await loadSOVGatewayData(searchQuery, resetError);
				
				if (gateways?.length > 0) {
					setTypeaheadData(transformSOVGatewayData(gateways));
				}
			}
		}
	});

	useDeepCompareEffect(() => {
		if (selectedFrom && selectedType && useAppState.brand !== BRANDS.SOV) {
			onChange(null);
			setIsLoading(true);
			getDestinations(
				useAppState.lang,
				selectedFrom.value,
				selectedType,
				useAppState.alias
			)
				.then(data => {
					const [list] = toFieldAdapter(data, useAppState.alias, {
						destinations: useAppState.getDictionary("destinations")
					});

					// Set initial gateway based on the gateway list + hotel list
					setInitialGateway(getInitialTo(list, previousSessionValue));

					setTypeaheadData(list);
				})
				.catch(err => console.error(err))
				.finally(() => {
					setIsLoading(false);
				});
		}
	}, [useAppState, selectedType, selectedFrom, previousSessionValue, onChange]);

	const placeHolderAndLabel = useAppState.getDictionary("going-to");

	return (
		<InputWrapper
			id={id}
			key={useAppState.lang}
			brand={useAppState.brand}
			label={placeHolderAndLabel}
			className={className}
			helperText={useAppState.getDictionary("select-a-destination")}
			error={error}
			hasSelectedItem={
				useAppState.brand === BRANDS.SOV ? !!isHasSelectedItem : !!selectedTo?.label
			}>
			{({ isFocused, handleOnFocus, handleOnBlur }) => (
				<Typeahead
					id={id}
					mode={mode}
					index={index}
					brand={useAppState.brand}
					error={error}
					isFocused={isFocused}
					isLoading={isLoading}
					isMultipleForm={!!selectedType}
					showAllSuggestionsOnClickIcon={true}
					isDropDownAutoScrollDisabled={useAppState.brand === BRANDS.SOV}
					isHasSelectedItem={useAppState.brand === BRANDS.SOV ? isHasSelectedItem : !!selectedTo}
					placeholder={
						useAppState.brand === BRANDS.SOV
							? placeHolderAndLabel
							: useAppState.getDictionary("destination")
					}
					data={typeaheadData}
					multipleFlightData={multipleFlightData}
					initialSelectedItem={initialGateway}
					selectedItem={selectedTo}
					setIsHasSelectedItem={setIsHasSelectedItem}
					handleSovMobileInputSelectionToView={handleSovMobileInputSelectionToView}
					onFocus={() => {
						resetError();
						handleOnFocus();
						handleFocus();
					}}
					onChange={value => {
						resetError();
						onChange?.(value);
						
						if(!value) {
							setNextButtonDisabled(true)
						}

						if(value?.label) {
							setNextButtonDisabled(false)
						}

						setValidateMultipleCities([
							{ from: true, to: true, date: true },
							{ from: true, to: true, date: true },
							{ from: true, to: true, date: true },
						]);

						if (isSetMultipleFrom?.isSetNextFrom) {
							setMultipleFlightData(prevData => ({
								...prevData,
								[`flight${isSetMultipleFrom?.level}`]: {
									...prevData[`flight${isSetMultipleFrom?.level}`],
									selectedFrom: value,
								},
								[`flight${index + 1}`]: {
									...prevData[`flight${index + 1}`],
									selectedTo: value,
								},
							}));
						}
					}}
					icon={
						useAppState.brand === BRANDS.SOV && (
							<span
								className={cx(classnames?.brand, {
									[classnames?.inputIcon]: useAppState.brand === BRANDS.SOV,
								})}
								focus-trap-toggle="true">
								<FontAwesomeIcon
									icon={faMapMarkerAlt}
									className={cx(classnames?.listViewIcon, classnames?.listViewIconGoingTo)}
								/>
							</span>
						)
					}
					iconClose={
						(inputValue, reset) =>
							useAppState.brand === BRANDS.SOV &&
							useAppState.sovSimplifiedSearchBar &&
							isOnlyOneTrue &&
							inputValue && (
								<button
									type="button"
									onClick={() => {
										reset();
										setNextButtonDisabled(true)
									}}
									className={cx(classnames.sovCloseIcon, classnames.brand)}
									focus-trap-toggle="true">
									<FontAwesomeIcon icon={faTimes} className={classnames.listViewIcon} />
								</button>
							)
						// eslint-disable-next-line react/jsx-curly-newline
					}
					onInput={handleSOVInput}
					onBlur={handleOnBlur}
					environment={useAppState.reactDOM}
					labels={{
						noResults: useAppState.getDictionary("no-results-found"),
					}}
				/>
			)}
		</InputWrapper>
	);
};

ToField.propTypes = {
	id: PropTypes.string,
	mode: PropTypes.string,
	index: PropTypes.number,
	className: PropTypes.string,
	selectedType: PropTypes.string,

	error: PropTypes.bool,
	isOnlyOneTrue: PropTypes.bool,

	isSetMultipleFrom: PropTypes.shape({
		level: PropTypes.number,
		isSetNextFrom: PropTypes.bool,
	}),

	classnames: PropTypes.shape({
		brand: PropTypes.string,
		listViewButton: PropTypes.string,
		inputIcon: PropTypes.string,
		listViewIcon: PropTypes.string,
		listViewIconGoingTo: PropTypes.string,
		sovCloseIcon: PropTypes.string,
	}),
	previousSessionValue: PropTypes.shape({
		label: PropTypes.string,
		value: PropTypes.string,
	}),
	selectedFrom: PropTypes.shape({
		label: PropTypes.string,
		value: PropTypes.string,
	}),
	selectedTo: PropTypes.shape({
		label: PropTypes.string,
		value: PropTypes.string,
	}),
	multipleFlightData: PropTypes.shape({
		flight1: PropTypes.shape({
			selectedFrom: PropTypes.shape({
				label: PropTypes.string,
				value: PropTypes.string,
			}),
			selectedTo: PropTypes.shape({
				label: PropTypes.string,
				value: PropTypes.string,
			}),
			selectedDates: PropTypes.oneOfType([
				PropTypes.arrayOf(PropTypes.instanceOf(dayjs)),
				PropTypes.arrayOf(PropTypes.string),
				PropTypes.instanceOf(dayjs),
				PropTypes.string,
			]),
		}),
		flight2: PropTypes.shape({
			selectedFrom: PropTypes.shape({
				label: PropTypes.string,
				value: PropTypes.string,
			}),
			selectedTo: PropTypes.shape({
				label: PropTypes.string,
				value: PropTypes.string,
			}),
			selectedDates: PropTypes.oneOfType([
				PropTypes.arrayOf(PropTypes.instanceOf(dayjs)),
				PropTypes.arrayOf(PropTypes.string),
				PropTypes.instanceOf(dayjs),
				PropTypes.string,
			]),
		}),
		flight3: PropTypes.shape({
			selectedFrom: PropTypes.shape({
				label: PropTypes.string,
				value: PropTypes.string,
			}),
			selectedTo: PropTypes.shape({
				label: PropTypes.string,
				value: PropTypes.string,
			}),
			selectedDates: PropTypes.oneOfType([
				PropTypes.arrayOf(PropTypes.instanceOf(dayjs)),
				PropTypes.arrayOf(PropTypes.string),
				PropTypes.instanceOf(dayjs),
				PropTypes.string,
			]),
		}),
	}),

	resetError: PropTypes.func,
	handleFocus: PropTypes.func,
	onChange: PropTypes.func.isRequired,
	setNextButtonDisabled: PropTypes.func,
	setMultipleFlightData: PropTypes.func,
	setValidateMultipleCities: PropTypes.func,
	handleSovMobileInputSelectionToView: PropTypes.func,
};

ToField.defaultProps = {
	id: undefined,
	mode: MODES.SEARCH,
	index: undefined,
	className: undefined,
	classnames: undefined,
	selectedType: undefined,

	error: false,
	isOnlyOneTrue: false,

	isSetMultipleFrom: undefined,
	selectedTo: undefined,
	selectedFrom: undefined,
	previousSessionValue: undefined,
	multipleFlightData: undefined,

	resetError: () => {},
	handleFocus: () => {},
	setNextButtonDisabled: () => {},
	setMultipleFlightData: () => {},
	setValidateMultipleCities: () => {},
	handleSovMobileInputSelectionToView: () => {},
};

export default ToField;
export { ToField };
